import React from 'react';
import { Clock, Globe } from 'lucide-react';
import TimePicker from '../../ui/TimePicker';
import type { UnifiedOnboardingData } from '../../../lib/onboarding/unifiedTypes';

interface TimingStepProps {
  data: UnifiedOnboardingData;
  onUpdate: (updates: Partial<UnifiedOnboardingData>) => void;
  onNext: () => void;
}

const timezones = [
  'America/New_York (EST)',
  'America/Chicago (CST)',
  'America/Denver (MST)',
  'America/Los_Angeles (PST)',
  'Europe/London (GMT)',
  'Europe/Paris (CET)',
  'Europe/Berlin (CET)',
  'Asia/Tokyo (JST)',
  'Asia/Shanghai (CST)',
  'Asia/Kolkata (IST)',
  'Australia/Sydney (AEDT)',
];

export default function TimingStep({ data, onUpdate, onNext }: TimingStepProps) {
  const handleTimeChange = (field: 'morningTime' | 'eveningTime', value: string) => {
    onUpdate({ [field]: value });
  };

  const handleTimezoneChange = (timezone: string) => {
    onUpdate({ timezone });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <Clock className="h-12 w-12 text-pink-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          When should we message you?
        </h3>
        <p className="text-gray-600">
          Set your preferred times for daily check-ins
        </p>
      </div>

      <div className="space-y-6">
        {/* Time Pickers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Clock className="h-5 w-5 text-pink-400" />
              <label className="font-medium text-gray-900">Morning Prompt</label>
            </div>
            <TimePicker
              value={data.morningTime}
              onChange={(value) => handleTimeChange('morningTime', value)}
              className="w-full"
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Clock className="h-5 w-5 text-pink-400" />
              <label className="font-medium text-gray-900">Evening Check-in</label>
            </div>
            <TimePicker
              value={data.eveningTime}
              onChange={(value) => handleTimeChange('eveningTime', value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Timezone Selector */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Globe className="h-5 w-5 text-pink-400" />
            <label className="font-medium text-gray-900">Timezone</label>
          </div>
          <select
            value={data.timezone}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent"
          >
            {timezones.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        {/* Preview */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h4 className="font-medium text-gray-900 mb-3">Your Schedule Preview</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Morning prompt:</span>
              <span className="font-medium">
                {new Date(`2000-01-01T${data.morningTime}`).toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Evening check-in:</span>
              <span className="font-medium">
                {new Date(`2000-01-01T${data.eveningTime}`).toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Timezone:</span>
              <span className="font-medium">{data.timezone}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}